import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

export function VoiceDictate({ onText, testid }) {
  const { lang, t } = useI18n();
  const [state, setState] = useState("idle"); // idle | recording | working
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tk) => tk.stop());
        await transcribe(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      mediaRef.current = mr;
      mr.start();
      setState("recording");
    } catch {
      toast.error(t("Microphone access denied"));
    }
  };

  const stop = () => { mediaRef.current?.stop(); setState("working"); };

  const transcribe = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, "dictation.webm");
      fd.append("language", lang);
      const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.text?.trim()) { onText(data.text.trim()); toast.success(t("Transcribed")); }
      else toast.error(t("Nothing heard — try again"));
    } catch {
      toast.error(t("Transcription failed"));
    } finally { setState("idle"); }
  };

  return (
    <button type="button" onClick={state === "recording" ? stop : (state === "idle" ? start : undefined)} disabled={state === "working"}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${state === "recording" ? "bg-brand text-white animate-pulse" : "bg-slate-900 text-white hover:bg-slate-700"}`}>
      {state === "working" ? <Loader2 size={13} className="animate-spin" /> : state === "recording" ? <Square size={13} /> : <Mic size={13} />}
      {state === "working" ? t("Transcribing…") : state === "recording" ? t("Stop") : t("Dictate")}
    </button>
  );
}
